import { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
    useGetAuditLogsByTypeQuery,
    useLazyGetAuditLogsByTypeQuery
} from "../../../store/services/tenancy.service";
import Table from "../../../components/Table/Table";
import { formatDateTime } from "../../../utils/formatDateTime";
import { Box, Button, CircularProgress, Typography } from "@mui/material";

export default function HistoryLogsTable() {
    const { tenancyId } = useSelector((state) => state.tenancy);
    const [pageSize] = useState(100);

    const {
        data: logsData,
        isLoading: initialLoading,
        error: logsError,
        isFetching
    } = useGetAuditLogsByTypeQuery({
        tenancyId,
        auditType: 'OnDemand', 
        limit: pageSize
    });

    const [loadMore, { isLoading: loadingMore }] = useLazyGetAuditLogsByTypeQuery();

    const [paginationModel, setPaginationModel] = useState({
        pageSize: pageSize,
        page: 0
    });

    const handleLoadMore = useCallback(async () => {
        if (logsData?.nextToken) {
            await loadMore({
                tenancyId,
                auditType: 'OnDemand', 
                limit: pageSize,
                nextToken: logsData.nextToken
            });
        }
    }, [logsData?.nextToken, loadMore, tenancyId, pageSize]);

    const headCells = [
        { id: 'waitingTime', label: 'Scheduled', initialVisible: true },
        { id: 'sentTypeName', label: 'Content', initialVisible: true },
        { id: 'sentType', label: 'Format', initialVisible: true },
        { id: 'sentToName', label: 'Recipients', initialVisible: true },
        { id: 'status', label: 'Status', initialVisible: true },
    ];

    const logs = logsData?.items || [];
    const formattedLogs = logs.map(log => ({
        ...log,
        waitingTime: formatDateTime(log.waitingTime)
    }));

    const isLoading = initialLoading || (isFetching && !logs.length);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Table
                name={'On-Demand History Logs'}
                data={formattedLogs}
                error={logsError}
                loading={isLoading}
                headCells={headCells}
                search={false}
                editColumn={false}
                checkbox={false}
                toolbarActions={null}
                showToolbarActions={false}
                selected={[]}
                setSelected={() => { }}
                maxTableHeight={"calc(100vh - 300px)"}
                initialVisibleColumns={headCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
            />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 2,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider'
                }}
            >
                {logsData?.hasMore && (
                    <Button
                        variant="outlined"
                        onClick={handleLoadMore}
                        disabled={loadingMore || isFetching}
                        startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                    >
                        {loadingMore ? 'Loading...' : 'Load More'}
                    </Button>
                )}

                <Typography variant="body2" color="text.secondary">
                    Showing {formattedLogs.length} records
                    {logsData?.hasMore && ' (more available)'}
                </Typography>
            </Box>
        </Box>
    );
}